import { valueIncludedOrEqual } from "@/utils/helpers";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCircleInfo, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Flex, Tooltip } from "@mantine/core";
import { flexRender, type Header } from "@tanstack/react-table";
import { Children } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { AssessmentQuestionColumnIds, BasicQuestionColumnIds } from "./columns";
import { WarningModal } from "./warning-modal";

export interface TableEmptyState {
  description: ReactNode;
  icon: IconDefinition;
  title: ReactNode;
}

interface TableWrapperProps<TData = unknown> {
  addQuestionDisabled?: boolean;
  addQuestionTestId: string;
  addQuestionTooltip?: ReactNode;
  addQuestionTooltipDisabled?: boolean;
  closeWarningModal: () => void;
  confirmDelete: () => void;
  emptyState?: TableEmptyState;
  hasRows?: boolean;
  header: ReactNode;
  isReadOnly: boolean;
  isUnframed?: boolean;
  onAddQuestionClick: () => void;
  tableHeaders: Header<TData, unknown>[];
  infoTooltipLabel: ReactNode;
  warningModalOpened: boolean;
}

export const TableWrapper = <TData = unknown,>({
  addQuestionDisabled = false,
  addQuestionTestId,
  addQuestionTooltip,
  addQuestionTooltipDisabled,
  children,
  closeWarningModal,
  confirmDelete,
  emptyState,
  hasRows: hasRowsOverride,
  header,
  isReadOnly,
  isUnframed = false,
  onAddQuestionClick,
  tableHeaders,
  infoTooltipLabel,
  warningModalOpened,
}: PropsWithChildren<TableWrapperProps<TData>>) => {
  const hasRows = hasRowsOverride ?? Children.count(children) > 0;

  return (
    <Flex
      direction="column"
      className={twMerge([
        "bg-white",
        isUnframed ? "p-0" : "rounded-md border border-gray-200 p-6",
      ])}
      gap="md"
    >
      <Flex
        className="w-full"
        direction="row"
        justify="space-between"
        align="center"
        gap="xs"
      >
        <Flex align="center" direction="row" gap="xs">
          <h2 className="text-lg font-semibold">{header}</h2>
          <Tooltip className="cursor-help" label={infoTooltipLabel}>
            <FontAwesomeIcon icon={faCircleInfo} className="text-uw-purple-3" />
          </Tooltip>
        </Flex>
      </Flex>
      {(hasRows || emptyState) && (
        <div className="border border-gray-200 rounded-md overflow-x-auto">
          <table className="w-full">
            <thead className="text-xs uppercase">
              <tr>
                {tableHeaders.map((header) => (
                  <th
                    key={header.id}
                    className={twMerge([
                      "px-4",
                      "py-3",
                      "text-left",
                      "font-semibold",
                      valueIncludedOrEqual(
                        [
                          BasicQuestionColumnIds.Type,
                          AssessmentQuestionColumnIds.Competency,
                        ],
                        header.id,
                      ) && "max-w-36 w-36",
                      valueIncludedOrEqual(
                        [
                          BasicQuestionColumnIds.Edit,
                          BasicQuestionColumnIds.Details,
                        ],
                        header.id,
                      ) && "max-w-24 w-24",
                      header.id === BasicQuestionColumnIds.Delete &&
                        "max-w-16 w-16",
                    ])}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hasRows ? (
                children
              ) : emptyState ? (
                <tr>
                  <td
                    className="border-t border-gray-200 px-4 py-9"
                    colSpan={tableHeaders.length}
                  >
                    <Flex
                      align="center"
                      className="mx-auto max-w-[360px] text-center"
                      direction="column"
                      gap={8}
                    >
                      <FontAwesomeIcon
                        className="mb-1 text-[26px] text-uw-gray-5"
                        icon={emptyState.icon}
                      />
                      <div className="text-sm font-semibold leading-5 text-uw-gray-7">
                        {emptyState.title}
                      </div>
                      <div className="text-sm font-medium leading-5 text-uw-gray-6">
                        {emptyState.description}
                      </div>
                    </Flex>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
      <Flex justify="flex-start" gap="xs">
        {!isReadOnly && (
          <Tooltip
            label={addQuestionTooltip}
            disabled={addQuestionTooltipDisabled}
          >
            <Button
              className="disabled:bg-transparent disabled:opacity-50 disabled:text-uw-purple p-0"
              data-testid={addQuestionTestId}
              disabled={addQuestionDisabled}
              onClick={onAddQuestionClick}
              rightSection={<FontAwesomeIcon icon={faPlus} />}
              size="xs"
              variant="transparent"
            >
              ADD QUESTION
            </Button>
          </Tooltip>
        )}
      </Flex>
      <WarningModal
        opened={warningModalOpened}
        onClose={closeWarningModal}
        onConfirm={confirmDelete}
      />
    </Flex>
  );
};
